import { Routes } from "router/routes";

/*
=================================
CREDIT PRICING
=================================

Precision runs a pure pay-as-you-assess credit model — there are no plans,
tiers or subscriptions. Every registered account gets `WEEKLY_CREDIT_ALLOWANCE`
free credits that refresh every week, and can top up at any time with purchased
credits that stay valid for `PURCHASED_CREDIT_VALIDITY_DAYS`.

This module is the single source of truth for every surface that quotes a credit
price (landing calculator, dashboard top-up modal, checkout summary) so the
numbers can never drift apart between them.
*/

/**
 * Free credits granted to every account, refreshed weekly.
 * These do not roll over, and are always spent before purchased credits.
 */
export const WEEKLY_CREDIT_ALLOWANCE = 100;

/** How long purchased (topped-up) credits remain spendable. */
export const PURCHASED_CREDIT_VALIDITY_DAYS = 90;

export type CreditCurrency = "NGN" | "USD";

export const CREDIT_CURRENCIES: CreditCurrency[] = ["NGN", "USD"];

export const CURRENCY_SYMBOLS: Record<CreditCurrency, string> = {
  NGN: "₦",
  USD: "$",
};

/**
 * Naira charged per credit — one flat rate at every volume.
 *
 * Credits are deliberately not bundled: buyers choose the exact amount their
 * hiring plan needs rather than being pushed onto a preset tier, and a credit
 * costs the same whether they buy 100 or 10,000.
 */
export const CREDIT_PRICE_NGN = 50;

/**
 * Dollars per credit. Published by the server alongside the naira rate — it is
 * a priced rate in its own right, not a currency conversion of the naira one.
 */
export const CREDIT_PRICE_USD = 0.0312;

/** Smallest purchase the checkout accepts. */
export const MIN_CREDIT_PURCHASE = 100;

/**
 * Largest purchase quotable at the flat rate. Volumes at or above the server's
 * `volume_discount_threshold` are a sales conversation, not a self-serve price.
 */
export const MAX_CREDIT_PURCHASE = 19_999;

/** Copy used when a buyer needs a larger bundle than self-serve supports. */
export const CREDIT_SALES_INQUIRY_SUBJECT = "Need more than 19,999 credits";

export const CREDIT_SALES_INQUIRY_MESSAGE = (requestedCredits?: number) => {
  const amountText =
    requestedCredits && requestedCredits > MAX_CREDIT_PURCHASE
      ? `${requestedCredits.toLocaleString()} credits`
      : "more than 19,999 credits";

  return `Hi Precision team, I'm trying to purchase ${amountText}. Please help me with a larger credit bundle.`;
};

export const buildCreditSalesContactUrl = (requestedCredits?: number) => {
  const searchParams = new URLSearchParams({
    subject: CREDIT_SALES_INQUIRY_SUBJECT,
    message: CREDIT_SALES_INQUIRY_MESSAGE(requestedCredits),
  });

  return `${Routes.contactUs}?${searchParams.toString()}`;
};

/** Buyers move in tens; there is no reason to price a single credit. */
export const CREDIT_PURCHASE_STEP = 10;

/** Fallback per-credit rates, superseded by the server's published pricing. */
export const CREDIT_PRICE_FALLBACK: Record<CreditCurrency, number> = {
  NGN: CREDIT_PRICE_NGN,
  USD: CREDIT_PRICE_USD,
};

/*
 * Indicative capacity, used only for the "create up to / screen up to" hints.
 * Derived from average end-to-end spend rather than hand-set per bundle, so the
 * figures stay consistent at any amount a buyer picks.
 */
const CREDITS_PER_JOB = 125;
const CREDITS_PER_CV_SCREEN = 12.5;

export const jobsForCredits = (credits: number) =>
  Math.max(1, Math.floor(credits / CREDITS_PER_JOB));

export const cvsForCredits = (credits: number) =>
  Math.floor(credits / CREDITS_PER_CV_SCREEN);

/**
 * Format an amount already expressed in `currency`. USD keeps two decimals
 * because converted values are rarely round; NGN is always whole naira.
 */
export const formatCurrency = (amount: number, currency: CreditCurrency) => {
  const value =
    currency === "NGN"
      ? Math.round(amount).toLocaleString()
      : amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${CURRENCY_SYMBOLS[currency]}${value}`;
};
